/**
 * 業務全体像（`getBusinessMap`）の TanStack Query ラッパー。
 *
 * 業務マップ（F1）と案件の変更一覧（F4）が同じ 4 レーンの姿を読むので、どちらの機能にも
 * 属さない場所に置く。
 *
 * 画面が `rpcClient` を直接叩かないための層である。Query キー（`query-keys.ts`）と入力の
 * 対応をここだけで決める。画面側で `useQuery` を直接書くと、同じ procedure に別のキーが
 * 付いた瞬間にキャッシュが分裂する。
 *
 * 案件を渡すと、その案件の未確定変更を重ねた姿（`side: 'after'`）を引く。渡さなければ
 * master だけを引く。**案件番号は number である**（作り直しの契約）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務全体像を TanStack Query のフックとして画面へ供給する。
 * @graph-connects rpcClient [calls] specGraph.getBusinessMap
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSpecLocale } from '../../i18n/spec-locale.js';
import { rpcClient } from '../../lib/orpc.js';
import { queryKeys } from '../../lib/query-keys.js';
import type { LaneSnapshot } from '../../lib/spec-graph-types/lanes.js';

/**
 * 業務全体像を取得する。
 *
 * @param pjNumber 重ねる案件番号。渡さなければ master
 * @returns ステージ │ 業務フロー │ 業務 │ 業務ルール の平らな 4 配列
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務全体像を、master または指定した案件を適用した姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.getBusinessMap
 */
export function useBusinessMap(pjNumber?: number): UseQueryResult<LaneSnapshot> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.getBusinessMap(locale, pjNumber),
    queryFn: () =>
      rpcClient.specGraph.getBusinessMap({
        ...(locale === undefined ? {} : { locale }),
        ...(pjNumber === undefined ? {} : { pj: { number: pjNumber, side: 'after' as const } }),
      }),
  });
}
